import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, EnvelopeSimple, Crown, UserGear, ArrowClockwise, ProhibitInset, PauseCircle, PlayCircle, Swap } from "@phosphor-icons/react";

const ROLE_META = {
  owner: { color: "#FFD60A", label: "owner" },
  admin: { color: "var(--accent)", label: "admin" },
  manager: { color: "#5AC8FA", label: "manager" },
  member: { color: "var(--text-2)", label: "member" },
};

const ROLE_RANK_ADMIN = ["admin"];  // only admins (or higher) can receive ownership transfers

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [newOrgName, setNewOrgName] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [lastInvite, setLastInvite] = useState(null);
  // Phase 6
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTarget, setTransferTarget] = useState("");
  const [transferConfirm, setTransferConfirm] = useState("");
  const [transferToken, setTransferToken] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await api.get("/organizations");
        setOrgs(r.data.organizations || []);
        if ((r.data.organizations || []).length > 0) setSelected(r.data.organizations[0]);
      } catch (e) { toast.error("Failed to load organizations"); }
    };
    run();
  }, []);

  useEffect(() => {
    if (!selected) { setMembers([]); setAnalytics(null); setPendingInvites([]); return; }
    const run = async () => {
      try {
        const canAdminHere = ["owner", "admin"].includes(selected.role);
        const [m, a, inv] = await Promise.all([
          api.get(`/organizations/${selected.id}/members`),
          api.get(`/organizations/${selected.id}/analytics`).catch(() => ({ data: null })),
          canAdminHere
            ? api.get(`/organizations/${selected.id}/invites?status=pending`).catch(() => ({ data: { invites: [] } }))
            : Promise.resolve({ data: { invites: [] } }),
        ]);
        setMembers(m.data.members || []);
        setAnalytics(a.data);
        setPendingInvites(inv.data.invites || []);
      } catch (e) { toast.error("Failed to load members"); }
    };
    run();
  }, [selected]);

  const createOrg = async () => {
    if (!newOrgName.trim()) return;
    setCreating(true);
    try {
      const r = await api.post("/organizations", { name: newOrgName.trim() });
      setOrgs([...orgs, r.data.organization]);
      setSelected(r.data.organization);
      setNewOrgName("");
      toast.success("Organization created");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setCreating(false); }
  };

  const refreshInvites = async () => {
    if (!selected) return;
    try {
      const r = await api.get(`/organizations/${selected.id}/invites?status=pending`);
      setPendingInvites(r.data.invites || []);
    } catch { /* ignore */ }
  };

  const invite = async () => {
    if (!selected) return;
    try {
      const r = await api.post(`/organizations/${selected.id}/invites`, { email: inviteEmail, role: inviteRole });
      setLastInvite(r.data);
      setInviteEmail("");
      toast.success(`Invited ${r.data.invite.email}`);
      refreshInvites();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const resendInvite = async (inv) => {
    try {
      const r = await api.post(`/organizations/${selected.id}/invites/${inv.id}/resend`);
      setLastInvite({ invite: { ...inv, expires_at: r.data.expires_at }, invite_url: r.data.invite_url, token: r.data.token });
      toast.success(`Resent invite to ${inv.email}`);
      refreshInvites();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invite for ${inv.email}?`)) return;
    try {
      await api.post(`/organizations/${selected.id}/invites/${inv.id}/revoke`);
      setPendingInvites(pendingInvites.filter((i) => i.id !== inv.id));
      toast.success("Revoked");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const changeRole = async (member, role) => {
    try {
      await api.put(`/organizations/${selected.id}/members/${member.id}/role`, { role });
      setMembers(members.map((m) => m.id === member.id ? { ...m, role } : m));
      toast.success("Role updated");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleActive = async (member) => {
    const action = member.status === "active" ? "deactivate" : "reactivate";
    if (!window.confirm(`${action[0].toUpperCase() + action.slice(1)} ${member.user?.email}?`)) return;
    try {
      await api.post(`/organizations/${selected.id}/members/${member.id}/${action}`);
      setMembers(members.map((m) => m.id === member.id ? { ...m, status: action === "deactivate" ? "disabled" : "active" } : m));
      toast.success(action === "deactivate" ? "Deactivated" : "Reactivated");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const removeMember = async (member) => {
    if (!window.confirm(`Remove ${member.user?.email}?`)) return;
    try {
      await api.delete(`/organizations/${selected.id}/members/${member.id}`);
      setMembers(members.filter((m) => m.id !== member.id));
      toast.success("Removed");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const initiateTransfer = async () => {
    if (!transferTarget || !transferConfirm) return;
    try {
      const r = await api.post(`/organizations/${selected.id}/transfer-ownership/initiate`, {
        to_member_id: transferTarget,
        confirmation: transferConfirm,
      });
      setTransferToken(r.data);
      toast.success("Transfer initiated — send the link to the new owner");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const myRole = selected?.role || "member";
  const canAdmin = ["owner", "admin"].includes(myRole);
  const isOwner = myRole === "owner";

  return (
    <div className="p-6 md:p-10" data-testid="organizations-page">
      <h1 className="text-3xl lg:text-4xl font-bold">// organizations</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Invite teammates to share contacts, campaigns and performance.</p>

      <section className="border p-4 mt-6" style={{ borderColor: "var(--border)" }} data-testid="orgs-list">
        <div className="overline mb-3">// my organizations</div>
        {orgs.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--text-2)" }}>You're not in any organization yet — create one below.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {orgs.map((o) => (
              <button
                key={o.id}
                onClick={() => setSelected(o)}
                className="tag"
                data-testid={`org-pill-${o.id}`}
                style={{
                  color: selected?.id === o.id ? "var(--bg)" : "var(--text-2)",
                  background: selected?.id === o.id ? "var(--accent)" : "transparent",
                  borderColor: selected?.id === o.id ? "var(--accent)" : "var(--border)",
                }}
              >{o.name} <span className="opacity-70 ml-1">({ROLE_META[o.role]?.label})</span></button>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <input
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            placeholder="New organization name"
            className="flex-1 px-3 py-2 border bg-transparent"
            style={{ borderColor: "var(--border)" }}
            data-testid="new-org-name"
          />
          <button onClick={createOrg} disabled={creating || !newOrgName.trim()} className="btn-primary flex items-center gap-2" data-testid="create-org-btn">
            <Plus weight="bold" /> Create
          </button>
        </div>
      </section>

      {selected && (
        <>
          <section className="border p-4 mt-6" style={{ borderColor: "var(--border)" }} data-testid="members-section">
            <div className="flex items-center justify-between mb-3">
              <div className="overline flex items-center gap-2"><UsersThree weight="bold" /> // members · {members.length}</div>
            </div>
            {canAdmin && (
              <div className="flex flex-wrap gap-2 mb-4">
                <input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  type="email"
                  placeholder="teammate@company.com"
                  className="flex-1 min-w-[220px] px-3 py-2 border bg-transparent"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="invite-email-input"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="px-3 py-2 border bg-transparent"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="invite-role-select"
                >
                  <option value="member">member</option>
                  <option value="manager">manager</option>
                  <option value="admin">admin</option>
                </select>
                <button onClick={invite} disabled={!inviteEmail} className="btn-primary flex items-center gap-2" data-testid="send-invite-btn">
                  <EnvelopeSimple weight="bold" /> Invite
                </button>
              </div>
            )}
            {lastInvite?.invite_url && (
              <div className="text-xs mb-3 p-2 border" style={{ color: "var(--text-2)", borderColor: "var(--border)" }} data-testid="last-invite-url">
                Share this link with {lastInvite.invite.email}: <code>{window.location.origin}{lastInvite.invite_url}</code>
              </div>
            )}
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {members.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-3 py-2"
                  data-testid={`member-row-${m.id}`}
                  style={{ opacity: m.status === "disabled" ? 0.45 : 1 }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">
                      {(m.user?.profile?.name) || m.user?.name || m.user?.email}
                      {m.status === "disabled" && <span className="ml-2 text-xs" style={{ color: "#FF9500" }}>(deactivated)</span>}
                    </div>
                    <div className="overline">{m.user?.email}</div>
                  </div>
                  {canAdmin && m.role !== "owner" ? (
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m, e.target.value)}
                      className="px-2 py-1 border bg-transparent text-xs"
                      style={{ borderColor: "var(--border)", color: ROLE_META[m.role]?.color }}
                      data-testid={`role-select-${m.id}`}
                    >
                      <option value="admin">admin</option>
                      <option value="manager">manager</option>
                      <option value="member">member</option>
                    </select>
                  ) : (
                    <span className="tag" style={{ color: ROLE_META[m.role]?.color, borderColor: ROLE_META[m.role]?.color }}>
                      {m.role === "owner" && <Crown weight="fill" className="inline mr-1" />}{ROLE_META[m.role]?.label}
                    </span>
                  )}
                  {canAdmin && m.role !== "owner" && (
                    <>
                      <button
                        onClick={() => toggleActive(m)}
                        className="btn-secondary text-xs px-2"
                        title={m.status === "active" ? "Deactivate" : "Reactivate"}
                        data-testid={`toggle-active-${m.id}`}
                      >
                        {m.status === "active" ? <PauseCircle weight="bold" /> : <PlayCircle weight="bold" />}
                      </button>
                      <button onClick={() => removeMember(m)} className="btn-secondary text-xs px-2" data-testid={`remove-member-${m.id}`}>
                        <Trash weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            {isOwner && members.filter((m) => m.role !== "owner" && m.status === "active" && ROLE_RANK_ADMIN.includes(m.role)).length > 0 && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                <button
                  onClick={() => setShowTransfer(true)}
                  className="btn-secondary flex items-center gap-2 text-sm"
                  data-testid="open-transfer-ownership-btn"
                >
                  <Swap weight="bold" /> Transfer ownership
                </button>
              </div>
            )}
          </section>

          {canAdmin && pendingInvites.length > 0 && (
            <section className="border p-4 mt-6" style={{ borderColor: "var(--border)" }} data-testid="pending-invites">
              <div className="overline mb-3 flex items-center gap-2"><EnvelopeSimple weight="bold" /> // pending invitations · {pendingInvites.length}</div>
              <div className="divide-y" style={{ borderColor: "var(--border)" }}>
                {pendingInvites.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-3 py-2" data-testid={`invite-row-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{inv.email}</div>
                      <div className="overline">
                        role: {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                        {inv.resent_at ? ` · resent ${new Date(inv.resent_at).toLocaleDateString()}` : ""}
                      </div>
                    </div>
                    <button onClick={() => resendInvite(inv)} className="btn-secondary text-xs px-2" title="Resend" data-testid={`resend-invite-${inv.id}`}>
                      <ArrowClockwise weight="bold" />
                    </button>
                    <button onClick={() => revokeInvite(inv)} className="btn-secondary text-xs px-2" title="Revoke" data-testid={`revoke-invite-${inv.id}`}>
                      <ProhibitInset weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {analytics && (
            <section className="border p-4 mt-6" style={{ borderColor: "var(--border)" }} data-testid="team-analytics">
              <div className="overline mb-3 flex items-center gap-2"><UserGear weight="bold" /> // team performance (last 30d)</div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                {Object.entries(analytics.totals || {}).map(([k, v]) => (
                  <div key={k} className="border p-3" style={{ borderColor: "var(--border)" }} data-testid={`totals-${k}`}>
                    <div className="overline">{k.replace(/_/g, " ")}</div>
                    <div className="text-2xl font-bold mt-1">{v}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ color: "var(--text-3)" }} className="text-left">
                      <th className="py-2">rep</th><th>role</th><th>contacts</th><th>replies 30d</th><th>meetings</th><th>tasks</th><th>campaigns</th><th>referrals</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                    {analytics.members.map((r) => (
                      <tr key={r.user_id} data-testid={`rep-row-${r.user_id}`}>
                        <td className="py-2 font-semibold">{r.name}</td>
                        <td style={{ color: ROLE_META[r.role]?.color }}>{r.role}</td>
                        <td>{r.contacts}</td>
                        <td>{r.replies_30d}</td>
                        <td>{r.meetings}</td>
                        <td>{r.tasks_completed}</td>
                        <td>{r.campaigns_active}</td>
                        <td>{r.referrals}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {analytics.campaigns?.length > 0 && (
                <div className="mt-6">
                  <div className="overline mb-2">// campaign performance</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ color: "var(--text-3)" }} className="text-left">
                          <th className="py-2">campaign</th><th>status</th><th>enrolled</th><th>completed</th><th>exited</th><th>reply rate</th><th>completion</th><th>failed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                        {analytics.campaigns.map((c) => (
                          <tr key={c.id}>
                            <td className="py-2 font-semibold truncate max-w-[220px]">{c.name}</td>
                            <td>{c.status}</td>
                            <td>{c.enrolled}</td>
                            <td>{c.completed}</td>
                            <td>{c.exited}</td>
                            <td>{c.reply_rate !== null ? `${c.reply_rate}%` : <span style={{ color: "var(--text-3)" }}>insufficient data</span>}</td>
                            <td>{c.completion_rate !== null ? `${c.completion_rate}%` : <span style={{ color: "var(--text-3)" }}>—</span>}</td>
                            <td>{c.failed > 0 ? <span style={{ color: "#FF3B30" }}>{c.failed}</span> : c.failed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {showTransfer && selected && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-50"
          style={{ background: "rgba(2,12,15,0.78)" }}
          onClick={() => { setShowTransfer(false); setTransferToken(null); setTransferTarget(""); setTransferConfirm(""); }}
          data-testid="transfer-ownership-modal"
        >
          <div
            className="border p-6 w-full max-w-lg"
            style={{ borderColor: "#FFD60A", background: "var(--surface)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overline flex items-center gap-2 mb-3" style={{ color: "#FFD60A" }}>
              <Crown weight="bold" /> // transfer ownership
            </div>
            {!transferToken ? (
              <>
                <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>
                  Only admins can receive ownership. You'll remain in the org as an admin after the transfer completes.
                </p>
                <div className="mb-3">
                  <div className="overline mb-1">new owner (admin)</div>
                  <select
                    value={transferTarget}
                    onChange={(e) => setTransferTarget(e.target.value)}
                    className="w-full px-2 py-2 border bg-transparent"
                    style={{ borderColor: "var(--border)" }}
                    data-testid="transfer-target-select"
                  >
                    <option value="">— select an admin —</option>
                    {members.filter((m) => m.role === "admin" && m.status === "active").map((m) => (
                      <option key={m.id} value={m.id}>{m.user?.email}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <div className="overline mb-1">type the organization name to confirm: <strong>{selected.name}</strong></div>
                  <input
                    value={transferConfirm}
                    onChange={(e) => setTransferConfirm(e.target.value)}
                    className="w-full px-2 py-2 border bg-transparent"
                    style={{ borderColor: "var(--border)" }}
                    data-testid="transfer-confirm-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowTransfer(false)} className="btn-secondary" data-testid="transfer-cancel-btn">Cancel</button>
                  <button
                    onClick={initiateTransfer}
                    disabled={!transferTarget || transferConfirm !== selected.name}
                    className="btn-primary disabled:opacity-40"
                    data-testid="transfer-initiate-btn"
                  >
                    Initiate transfer
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm mb-3" style={{ color: "var(--text-2)" }}>
                  Transfer initiated. Send this acceptance link to the new owner — it expires in 3 days.
                </p>
                <div className="p-2 border mb-3 text-xs" style={{ borderColor: "var(--border)", color: "var(--text-2)" }} data-testid="transfer-accept-url">
                  <code>{window.location.origin}{transferToken.accept_url}</code>
                </div>
                <div className="flex justify-end">
                  <button onClick={() => { setShowTransfer(false); setTransferToken(null); setTransferTarget(""); setTransferConfirm(""); }} className="btn-primary" data-testid="transfer-done-btn">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
